import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Save, RotateCcw, Zap, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

interface ProviderConfig {
  id: string;
  task_key: string;
  label: string;
  description: string | null;
  provider: string;
  primary_model: string;
  fallback_models: string[];
  fallback_providers: string[];
  temperature: number;
  reasoning_effort: string | null;
  enabled: boolean;
  notes: string | null;
  updated_at: string;
}

interface RegisteredProvider {
  key: string;
  label: string;
  env_var: string;
  configured: boolean;
  models: string[];
}

const cardStyle = { background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" } as const;
const inputStyle = { background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 95%)" } as const;

const ProviderConfigTable = () => {
  const [rows, setRows] = useState<ProviderConfig[]>([]);
  const [providers, setProviders] = useState<RegisteredProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState<Record<string, Partial<ProviderConfig>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; ms?: number; msg: string }>>({});

  const load = async () => {
    setLoading(true);
    const [cfgRes, provRes] = await Promise.all([
      supabase.from("ai_provider_config").select("*").order("task_key"),
      supabase.functions.invoke("ai-test-provider", { method: "GET" } as never),
    ]);
    if (cfgRes.error) toast.error("Failed to load AI config: " + cfgRes.error.message);
    else setRows((cfgRes.data ?? []) as ProviderConfig[]);
    const provData = (provRes.data as { providers?: RegisteredProvider[] } | null)?.providers;
    if (provData) setProviders(provData);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const patch = (id: string, p: Partial<ProviderConfig>) => {
    setDirty((d) => ({ ...d, [id]: { ...(d[id] ?? {}), ...p } }));
  };
  const merged = (r: ProviderConfig): ProviderConfig => ({ ...r, ...(dirty[r.id] ?? {}) });
  const isDirty = (id: string) => !!dirty[id] && Object.keys(dirty[id]).length > 0;

  const save = async (r: ProviderConfig) => {
    const m = merged(r);
    setSaving(r.id);
    const { error } = await supabase
      .from("ai_provider_config")
      .update({
        provider: m.provider,
        primary_model: m.primary_model,
        fallback_models: m.fallback_models,
        fallback_providers: m.fallback_providers,
        temperature: m.temperature,
        reasoning_effort: m.reasoning_effort,
        enabled: m.enabled,
        notes: m.notes,
      })
      .eq("id", r.id);
    setSaving(null);
    if (error) toast.error("Save failed: " + error.message);
    else {
      toast.success(`Saved ${m.label}`);
      setDirty((d) => { const n = { ...d }; delete n[r.id]; return n; });
      load();
    }
  };
  const reset = (id: string) => setDirty((d) => { const n = { ...d }; delete n[id]; return n; });

  const testConnection = async (r: ProviderConfig) => {
    const m = merged(r);
    setTesting(r.id);
    try {
      const { data, error } = await supabase.functions.invoke("ai-test-provider", {
        body: { provider: m.provider, model: m.primary_model },
      });
      if (error) throw error;
      const res = data as { ok: boolean; latency_ms?: number; error?: string; sample?: string };
      setTestResult((t) => ({
        ...t,
        [r.id]: {
          ok: !!res.ok,
          ms: res.latency_ms,
          msg: res.ok ? `OK in ${res.latency_ms}ms, "${res.sample ?? ""}"` : (res.error ?? "Failed"),
        },
      }));
      if (res.ok) toast.success(`${m.provider} / ${m.primary_model} reachable (${res.latency_ms}ms)`);
      else toast.error(`Test failed: ${res.error}`);
    } catch (e) {
      const msg = (e as Error).message ?? "Test failed";
      setTestResult((t) => ({ ...t, [r.id]: { ok: false, msg } }));
      toast.error(msg);
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm" style={{ color: "hsl(0 0% 55%)" }}>
        <Loader2 size={14} className="animate-spin" /> Loading AI configuration…
      </div>
    );
  }

  const providerOptions = providers.length ? providers : [
    { key: "lovable_gateway", label: "Lovable AI Gateway", env_var: "LOVABLE_API_KEY", configured: true, models: [] },
    { key: "google_direct", label: "Gemini Direct (Google API)", env_var: "GEMINI_API_KEY", configured: true, models: [] },
  ];

  return (
    <div className="space-y-3">
      <div className="rounded-lg border p-3" style={cardStyle}>
        <div className="text-[11px] uppercase tracking-wide mb-2" style={{ color: "hsl(0 0% 50%)" }}>
          Registered providers
        </div>
        <div className="flex flex-wrap gap-2">
          {providerOptions.map((p) => (
            <div key={p.key} className="flex items-center gap-1.5 px-2 py-1 rounded text-xs"
              style={{ background: "hsl(0 0% 12%)", color: "hsl(0 0% 80%)" }}>
              {p.configured
                ? <CheckCircle2 size={12} style={{ color: "hsl(140 60% 55%)" }} />
                : <XCircle size={12} style={{ color: "hsl(0 70% 60%)" }} />}
              <span>{p.label}</span>
              <code className="text-[10px] opacity-60">{p.env_var}</code>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>
        Each task has a primary <strong>provider</strong> + <strong>model</strong> + ordered model fallbacks.
        Provider fallbacks let a task fail over to a second provider (e.g. Gemini Direct) if the primary fails.
        Config changes propagate to edge functions within ~60s.
      </p>

      {rows.map((r) => {
        const m = merged(r);
        const activeProvider = providerOptions.find((p) => p.key === m.provider);
        const modelOpts = activeProvider?.models ?? [];
        const tr = testResult[r.id];
        return (
          <div key={r.id} className="rounded-xl border p-4" style={cardStyle}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs px-1.5 py-0.5 rounded" style={{ background: "hsl(0 0% 12%)", color: "hsl(207 68% 65%)" }}>{r.task_key}</code>
                  <span className="font-medium text-sm" style={{ color: "hsl(0 0% 95%)" }}>{r.label}</span>
                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ background: "hsl(0 0% 12%)", color: "hsl(42 55% 60%)" }}>
                    {activeProvider?.label ?? m.provider}
                  </span>
                  {!m.enabled && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ background: "hsl(0 50% 20%)", color: "hsl(0 80% 75%)" }}>disabled</span>}
                </div>
                {r.description && <p className="text-xs mt-1" style={{ color: "hsl(0 0% 55%)" }}>{r.description}</p>}
              </div>
              <label className="flex items-center gap-2 text-xs whitespace-nowrap" style={{ color: "hsl(0 0% 70%)" }}>
                <input type="checkbox" checked={m.enabled} onChange={(e) => patch(r.id, { enabled: e.target.checked })} />
                Enabled
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Primary provider</label>
                <select className="w-full rounded border px-2 py-1.5 text-sm" style={inputStyle}
                  value={m.provider}
                  onChange={(e) => {
                    const newProv = e.target.value;
                    const p = providerOptions.find((x) => x.key === newProv);
                    const newModel = p?.models[0] ?? m.primary_model;
                    patch(r.id, { provider: newProv, primary_model: newModel, fallback_models: [] });
                  }}>
                  {providerOptions.map((p) => (
                    <option key={p.key} value={p.key} disabled={!p.configured}>
                      {p.label} {p.configured ? "" : "(not configured)"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Primary model</label>
                {modelOpts.length > 0 ? (
                  <select className="w-full rounded border px-2 py-1.5 text-sm" style={inputStyle}
                    value={m.primary_model}
                    onChange={(e) => patch(r.id, { primary_model: e.target.value })}>
                    {modelOpts.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
                    {!modelOpts.includes(m.primary_model) && <option value={m.primary_model}>{m.primary_model} (custom)</option>}
                  </select>
                ) : (
                  <input className="w-full rounded border px-2 py-1.5 text-sm font-mono" style={inputStyle}
                    value={m.primary_model}
                    onChange={(e) => patch(r.id, { primary_model: e.target.value })} />
                )}
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>
                  Model fallbacks (same provider, tried in order on 5xx)
                </label>
                <input className="w-full rounded border px-2 py-1.5 text-sm font-mono" style={inputStyle}
                  placeholder="model1,model2"
                  value={(m.fallback_models ?? []).join(", ")}
                  onChange={(e) => patch(r.id, {
                    fallback_models: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })} />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>
                  Provider fallbacks (tried after all models exhausted)
                </label>
                <select multiple size={Math.min(3, providerOptions.length)} className="w-full rounded border px-2 py-1.5 text-sm" style={inputStyle}
                  value={m.fallback_providers ?? []}
                  onChange={(e) => {
                    const picked = Array.from(e.target.selectedOptions).map((o) => o.value);
                    patch(r.id, { fallback_providers: picked });
                  }}>
                  {providerOptions.filter((p) => p.key !== m.provider).map((p) => (
                    <option key={p.key} value={p.key} disabled={!p.configured}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Temperature</label>
                <input type="number" min={0} max={2} step={0.05}
                  className="w-full rounded border px-2 py-1.5 text-sm" style={inputStyle}
                  value={m.temperature}
                  onChange={(e) => patch(r.id, { temperature: Number(e.target.value) })} />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Reasoning effort (OpenAI GPT-5+ only)</label>
                <select className="w-full rounded border px-2 py-1.5 text-sm" style={inputStyle}
                  value={m.reasoning_effort ?? ""}
                  onChange={(e) => patch(r.id, { reasoning_effort: e.target.value || null })}>
                  <option value="">(none)</option>
                  <option value="minimal">minimal</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] uppercase tracking-wide mb-1" style={{ color: "hsl(0 0% 50%)" }}>Admin notes</label>
                <textarea className="w-full rounded border px-2 py-1.5 text-sm" style={inputStyle} rows={2}
                  value={m.notes ?? ""}
                  onChange={(e) => patch(r.id, { notes: e.target.value })}
                  placeholder="Why this model? When to escalate?" />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => testConnection(r)}
                  disabled={testing === r.id}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border"
                  style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 80%)" }}
                >
                  {testing === r.id ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                  Test connection
                </button>
                {tr && (
                  <span className="text-xs" style={{ color: tr.ok ? "hsl(140 60% 60%)" : "hsl(0 70% 65%)" }}>
                    {tr.ok ? "✓" : "✗"} {tr.msg}
                  </span>
                )}
              </div>
              {isDirty(r.id) && (
                <div className="flex items-center gap-2">
                  <button onClick={() => reset(r.id)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border"
                    style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 70%)" }}>
                    <RotateCcw size={12} /> Revert
                  </button>
                  <button onClick={() => save(r)} disabled={saving === r.id}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded"
                    style={{ background: "hsl(207 68% 50%)", color: "hsl(0 0% 100%)" }}>
                    {saving === r.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Save
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProviderConfigTable;
